/**
 * exitImpersonation orchestration.
 *
 * Phase 3.1 Plan 03 (per ADR-006 section 3 + ADR-005):
 *   Close the impersonation session and restore the staff token pair so
 *   the SPA re-registers under the admin route surface.
 *
 *     1. Best-effort POST /v1/admin/impersonate/exit. Per ADR-005 the
 *        endpoint is idempotent 200 and the response is informational only.
 *        Network failures and non-200 responses are SWALLOWED here; the
 *        server may have already closed the session (e.g., the 1-hour cap
 *        elapsed while the user was on a stale tab) and we still want to
 *        swap back to staff locally regardless.
 *     2. Read staffSidecar; if absent we are in a corrupted state. Clear
 *        all tokens and throw ExitImpersonationError; the caller (typically
 *        AuthProvider) treats this as a session-expiry signal.
 *     3. Replace authStorage active pair with the staff sidecar pair (real
 *        staff access + real staff refresh; the IMPERSONATION_NO_REFRESH
 *        sentinel is replaced in this step).
 *     4. Clear staffSidecar.
 *     5. Refetch /v1/auth/me; on success returns the staff User object.
 *        On 401 (the staff session also expired during the impersonation
 *        window): clear all tokens and throw.
 */
import { authStorage, staffSidecar } from '@/lib/auth-storage';
import { meRequest } from '@/features/auth/api';
import { exitImpersonationRequest } from './api';
import { ExitImpersonationError } from './schemas';
import type { User } from '@/features/auth/schemas';

/**
 * Close the active impersonation session and restore staff tokens.
 *
 * @returns The post-swap User (the original staff actor)
 * @throws ExitImpersonationError if sidecar is absent or staff /me 401s
 */
export async function exitImpersonation(): Promise<User> {
  // Step 1: best-effort POST /exit. exitImpersonationRequest swallows
  // network errors and non-200 responses by returning null; we ignore the
  // return value entirely.
  await exitImpersonationRequest();

  // Step 2: restore staff from sidecar. Absent sidecar is the corrupted
  // state we cannot recover from.
  const staff = staffSidecar.get();
  if (!staff) {
    authStorage.clear();
    throw new ExitImpersonationError(
      'sidecar_absent',
      'Cannot exit impersonation: staff sidecar absent',
    );
  }

  // Step 3: replace the active tokens with the real staff pair. The
  // IMPERSONATION_NO_REFRESH sentinel that was in the active refresh slot
  // is overwritten here.
  authStorage.set(staff.access, staff.refresh);

  // Step 4: clear the sidecar; the staff pair is no longer the "saved"
  // snapshot, it is the active pair.
  staffSidecar.clear();

  // Step 5: refetch /me. On 401 the staff session expired during the
  // impersonation window; clear everything and throw.
  try {
    const me = await meRequest();
    return me;
  } catch (err) {
    authStorage.clear();
    throw new ExitImpersonationError(
      'staff_me_failed',
      err instanceof Error ? err.message : 'staff_me_failed',
    );
  }
}
